import React, { useCallback, useEffect, useState, createRef, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageDown, Layers, AlertTriangle, Lock, ChevronDown } from 'lucide-react';

import { Checkbox } from '../../../../app/src/shared/custom-ui';

const FILE_TYPE_OPTIONS = [
    {
        value: 'jpg',
        label: 'jpg',
    },
    {
        value: 'png',
        label: 'png',
    },
];

const DEFAULT_FILENAME = 'image';
const REFRESH_VIEWPORT_TIMEOUT = 100;

/* ─── Shared field styles ────────────────────────────────────────── */
const fieldCls = [
    'w-full rounded-[var(--v2-radius-md)] px-3.5 py-[9px] min-h-[40px]',
    'text-black text-[13.5px] font-medium',
    'border border-[var(--v2-border-glass-strong)]',
    'placeholder:text-[var(--v2-text-muted)]',
    'outline-none transition-all duration-[var(--v2-duration-fast)]',
    'hover:border-[var(--v2-primary-400)]/50',
    'shadow-[0_1px_3px_rgba(0,0,0,0.15),inset_0_1px_0_rgba(255,255,255,0.04)]',
    'disabled:opacity-50 disabled:cursor-not-allowed',
].join(' ');

const ViewportDownloadForm = ({
    activeViewportElement,
    onClose,
    updateViewportPreview,
    enableViewport,
    disableViewport,
    toggleAnnotations,
    toggleWarning,
    loadImage,
    downloadBlob,
    defaultSize,
    minimumSize,
    maximumSize,
    canvasClass,
}) => {
    const { t } = useTranslation('Modals');

    const [filename, setFilename] = useState(DEFAULT_FILENAME);
    const [fileType, setFileType] = useState(['jpg']);

    const [dimensions, setDimensions] = useState({
        width: defaultSize,
        height: defaultSize,
    });

    const [showAnnotations, setShowAnnotations] = useState(true);
    const [showWarning, setShowWarning] = useState(true);
    const [keepAspect, setKeepAspect] = useState(true);
    const [aspectMultiplier, setAspectMultiplier] = useState({
        width: 1,
        height: 1,
    });

    const [viewportElement, setViewportElement] = useState<HTMLDivElement | null>(null);
    const [viewportElementDimensions, setViewportElementDimensions] = useState({
        width: defaultSize,
        height: defaultSize,
    });

    const [downloadCanvas, setDownloadCanvas] = useState({
        ref: createRef(),
        width: defaultSize,
        height: defaultSize,
    });

    const [viewportPreview, setViewportPreview] = useState({
        src: null,
        width: defaultSize,
        height: defaultSize,
    });

    const [error, setError] = useState({
        width: false,
        height: false,
        filename: false,
    });

    const hasError = Object.values(error).includes(true);

    const refreshViewport = useRef(null);

    const onKeepAspectToggle = () => {
        const { width, height } = dimensions;
        if (!keepAspect) {
            const aspectMultiplier = {
                width: width / height,
                height: height / width,
            };
            setAspectMultiplier(aspectMultiplier);
        }

        setKeepAspect(!keepAspect);
    };

    const downloadImage = () => {
        downloadBlob(
            filename || DEFAULT_FILENAME,
            fileType,
            viewportElement,
            downloadCanvas.ref.current
        );
    };

    /**
     * @param {object} value - Input value
     * @param {string} dimension - "height" | "width"
     */
    const onDimensionsChange = (value, dimension) => {
        const oppositeDimension = dimension === 'height' ? 'width' : 'height';
        const sanitizedTargetValue = value.replace(/\D/, '');
        const isEmpty = sanitizedTargetValue === '';
        const newDimensions = { ...dimensions };
        const updatedDimension = isEmpty ? '' : Math.min(sanitizedTargetValue, maximumSize);

        if (updatedDimension === dimensions[dimension]) return;

        newDimensions[dimension] = updatedDimension;
        if (keepAspect && newDimensions[oppositeDimension] !== '') {
            newDimensions[oppositeDimension] = Math.round(
                newDimensions[dimension] * aspectMultiplier[oppositeDimension]
            );
        }
        setDimensions(newDimensions);
        if (!isEmpty) {
            setViewportElementDimensions(newDimensions);
            setDownloadCanvas(s => ({ ...s, ...newDimensions }));
        }
    };

    const validSize = useCallback(v => (v >= minimumSize ? v : minimumSize), [minimumSize]);

    const loadAndUpdateViewports = useCallback(async () => {
        const { width: scaledWidth, height: scaledHeight } = await loadImage(
            activeViewportElement, viewportElement, dimensions.width, dimensions.height
        );
        toggleAnnotations(showAnnotations, viewportElement, activeViewportElement);
        toggleWarning(showWarning);

        const scaledDimensions = { height: validSize(scaledHeight), width: validSize(scaledWidth) };
        setViewportElementDimensions(scaledDimensions);
        setDownloadCanvas(s => ({ ...s, ...scaledDimensions }));

        const { dataUrl, width: vpW, height: vpH } = await updateViewportPreview(
            viewportElement, downloadCanvas.ref.current, fileType
        );
        setViewportPreview(s => ({ ...s, src: dataUrl, width: validSize(vpW), height: validSize(vpH) }));
    }, [
        loadImage, activeViewportElement, viewportElement, dimensions.width, dimensions.height,
        toggleAnnotations, toggleWarning, showAnnotations, showWarning, validSize,
        updateViewportPreview, downloadCanvas.ref, fileType,
    ]);

    useEffect(() => {
        enableViewport(viewportElement);
        return () => { disableViewport(viewportElement); };
    }, [disableViewport, enableViewport, viewportElement]);

    useEffect(() => {
        if (refreshViewport.current !== null) clearTimeout(refreshViewport.current);
        refreshViewport.current = setTimeout(() => {
            refreshViewport.current = null;
            loadAndUpdateViewports();
        }, REFRESH_VIEWPORT_TIMEOUT);
    }, [
        activeViewportElement, viewportElement, showAnnotations, showWarning, dimensions,
        loadImage, toggleAnnotations, toggleWarning, updateViewportPreview, fileType,
        downloadCanvas.ref, minimumSize, maximumSize, loadAndUpdateViewports,
    ]);

    useEffect(() => {
        setError({
            width: dimensions.width < minimumSize,
            height: dimensions.height < minimumSize,
            filename: !filename,
        });
    }, [dimensions, filename, minimumSize]);

    /* ─── label helper ──────────────────────────────────────────── */
    const Label: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
        <span className={`block mb-1.5 text-[12px] font-semibold tracking-[0.04em] uppercase text-[var(--v2-text-muted)] ${className}`}>
            {children}
        </span>
    );

    return (
        <div className="flex w-full flex-col lg:flex-row gap-6">

            {/* ── LEFT: Image Preview ──────────────────────────────── */}
            <div className="relative flex flex-1 items-center justify-center rounded-[var(--v2-radius-xl)] bg-black p-4 sm:flex-[7]">
                {/* subtle glass ring */}
                <div className="pointer-events-none absolute inset-0 rounded-[var(--v2-radius-xl)] ring-1 ring-white/10 z-10" />

                {/* ⚠ EXACT original viewport div — fixed pixel height required by Cornerstone */}
                <div className="relative h-[512px] w-[512px] overflow-auto">
                    {activeViewportElement ? (
                        <div
                            className="mx-auto"
                            style={{
                                width: viewportElementDimensions.width,
                                height: '480px',
                            }}
                            ref={ref => setViewportElement(ref)}
                        />
                    ) : (
                        <div className="flex h-full flex-col items-center justify-center gap-3 text-[var(--v2-text-muted)] text-center">
                            <ImageDown className="h-10 w-10 opacity-40" />
                            <span className="text-[14px]">Active viewport has no displayed image</span>
                        </div>
                    )}
                </div>
            </div>

            {/* ── RIGHT: Form ──────────────────────────────────────── */}
            <div className="w-full lg:w-[260px] shrink-0 flex flex-col gap-5">

                {/* File name + type */}
                <div>
                    <Label>{t('File name')}</Label>
                    <div className="flex items-start gap-2">
                        <div className="flex-1 min-w-0">
                            <input
                                value={filename}
                                onChange={evt => setFilename(evt.target.value)}
                                placeholder="image"
                                className={fieldCls}
                            />
                            {error.filename && (
                                <p className="mt-1 text-[11.5px] text-[var(--v2-danger-500)]">
                                    File name cannot be empty.
                                </p>
                            )}
                        </div>
                        {/* File type select */}
                        <div className="w-[76px] shrink-0 relative">
                            <select
                                value={fileType[0]}
                                onChange={e => setFileType([e.target.value])}
                                className={[fieldCls, 'appearance-none pr-7 cursor-pointer'].join(' ')}
                            >
                                {FILE_TYPE_OPTIONS.map(o => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </select>
                            <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--v2-text-muted)]" />
                        </div>
                    </div>
                </div>

                {/* Separator */}
                <div className="h-px bg-[var(--v2-border-subtle)]" />

                {/* Dimensions */}
                <div>
                    <Label>
                        Image size
                        <span className="ml-1 normal-case font-normal text-[11px]">px</span>
                    </Label>
                    <div className="flex items-start gap-3">
                        <div className="flex-1">
                            <span className="block mb-1 text-[11px] font-semibold text-[var(--v2-text-muted)] uppercase tracking-[0.04em]">W</span>
                            <input
                                type="number"
                                value={dimensions.width}
                                onChange={evt => onDimensionsChange(evt.target.value, 'width')}
                                className={fieldCls}
                            />
                            {error.width && (
                                <p className="mt-1 text-[11.5px] text-[var(--v2-danger-500)]">Min 100 px</p>
                            )}
                        </div>
                        <div className="flex-1">
                            <span className="block mb-1 text-[11px] font-semibold text-[var(--v2-text-muted)] uppercase tracking-[0.04em]">H</span>
                            <input
                                type="number"
                                value={dimensions.height}
                                onChange={evt => onDimensionsChange(evt.target.value, 'height')}
                                className={fieldCls}
                            />
                            {error.height && (
                                <p className="mt-1 text-[11.5px] text-[var(--v2-danger-500)]">Min 100 px</p>
                            )}
                        </div>
                    </div>

                    {/* Keep aspect toggle */}
                    <button
                        type="button"
                        onClick={onKeepAspectToggle}
                        className={[
                            'mt-2.5 flex items-center gap-1.5 text-[12px] font-medium border-0 bg-transparent cursor-pointer p-0 transition-colors duration-150',
                            keepAspect
                                ? 'text-[var(--v2-primary-500)]'
                                : 'text-[var(--v2-text-muted)] hover:text-[var(--v2-text-secondary)]',
                        ].join(' ')}
                    >
                        <Lock className="h-3 w-3" />
                        {keepAspect ? 'Aspect locked' : 'Lock aspect ratio'}
                    </button>
                </div>

                {/* Separator */}
                <div className="h-px bg-[var(--v2-border-subtle)]" />

                {/* Options */}
                <div className="flex flex-col gap-3">
                    <Label>Options</Label>
                    <div className="flex items-center gap-2.5">
                        <Checkbox
                            checked={showAnnotations}
                            onChange={setShowAnnotations}
                            label=""
                        />
                        <span className="flex items-center gap-1.5 text-[13.5px] text-[var(--v2-text-secondary)] cursor-pointer select-none"
                            onClick={() => setShowAnnotations(v => !v)}>
                            <Layers className="h-3.5 w-3.5 text-[var(--v2-primary-400)] shrink-0" />
                            Include annotations
                        </span>
                    </div>
                    <div className="flex items-center gap-2.5">
                        <Checkbox
                            checked={showWarning}
                            onChange={setShowWarning}
                            label=""
                        />
                        <span className="flex items-center gap-1.5 text-[13.5px] text-[var(--v2-text-secondary)] cursor-pointer select-none"
                            onClick={() => setShowWarning(v => !v)}>
                            <AlertTriangle className="h-3.5 w-3.5 text-[var(--v2-warning)] shrink-0" />
                            Include warning message
                        </span>
                    </div>
                </div>

                {/* Spacer pushes buttons to bottom */}
                <div className="flex-1" />

                {/* Action buttons */}
                <div className="flex items-center justify-end gap-2.5 pt-1">
                    {/* Ghost cancel */}
                    <button
                        type="button"
                        onClick={onClose}
                        className={[
                            'px-4 py-2 rounded-[var(--v2-radius-md)] text-[13.5px] font-medium',
                            'border border-[var(--v2-border-glass-strong)] bg-transparent',
                            'text-[var(--v2-text-secondary)]',
                            'cursor-pointer transition-all duration-[var(--v2-duration-fast)]',
                            'hover:bg-[var(--v2-bg-hover)] hover:text-[var(--v2-text-primary)] hover:border-[var(--v2-border-card)]',
                        ].join(' ')}
                    >
                        Cancel
                    </button>

                    {/* Premium Download button */}
                    <button
                        type="button"
                        disabled={hasError}
                        onClick={downloadImage}
                        className={[
                            'flex items-center gap-2 px-5 py-2 rounded-[var(--v2-radius-md)]',
                            'text-white text-[13.5px] font-semibold tracking-[-0.01em]',
                            'border border-[var(--v2-primary-400)]/40',
                            'cursor-pointer transition-all duration-200',
                            'hover:brightness-110 active:scale-[0.97]',
                            'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:brightness-100',
                        ].join(' ')}
                        style={{
                            background: 'linear-gradient(135deg, var(--v2-primary-500) 0%, var(--v2-primary-700) 100%)',
                            boxShadow: hasError
                                ? 'none'
                                : '0 4px 14px rgba(59,130,246,0.40), 0 1px 3px rgba(0,0,0,0.20), inset 0 1px 0 rgba(255,255,255,0.15)',
                        }}
                    >
                        <ImageDown className="h-[15px] w-[15px] shrink-0" />
                        Download
                    </button>
                </div>
            </div>
        </div >
    );
};

export default ViewportDownloadForm;
